{
  "$schema": "https://json-schema.org/draft/2020-12/schema",
  "$id": "https://raw.githubusercontent.com/jwildfire/safety.viz/main/src/data/schema/ae-explorer.json",
  "title": "safety.viz ae-explorer data contract",
  "description": "Adverse-event data: one record per adverse event, plus one placeholder row per participant with no adverse events so the population denominator is right (AE-DATA-001). Placeholder rows are identified by the placeholder_flag setting — blank or 'NA' in the System Organ Class column by default — and count toward participant denominators while never appearing as events. Column names default to the ADaM ADAE standard and are remappable through the settings (AE-CFG-003); a chart initializes with no settings at all when the data carries the default columns (AE-DATA-003).",
  "type": "object",
  "required": ["data", "settings"],
  "properties": {
    "data": {
      "type": "array",
      "minItems": 1,
      "items": { "type": "object" },
      "description": "d3.csv()-style records; every row carries the participant, System Organ Class, Preferred Term, and treatment-group columns named in settings."
    },
    "settings": {
      "type": "object",
      "description": "Column mappings and rendering options; merged onto the module's DEFAULT_SETTINGS, so only overrides need to be supplied (AE-CFG-001).",
      "required": ["id_col", "major_col", "minor_col", "group_col"],
      "properties": {
        "id_col": {
          "type": "string",
          "default": "USUBJID",
          "description": "Participant identifier column; required in data. Distinct participants drive the group denominators and participant-mode numerators."
        },
        "major_col": {
          "type": "string",
          "default": "AEBODSYS",
          "description": "Major category column — the MedDRA System Organ Class; required in data. One expandable table section per level, and the default placeholder-flag column."
        },
        "minor_col": {
          "type": "string",
          "default": "AEDECOD",
          "description": "Minor category column — the MedDRA Preferred Term; required in data. One nested row per level under its System Organ Class."
        },
        "group_col": {
          "type": "string",
          "default": "ARM",
          "description": "Treatment group column; required in data. One rate column per level (up to max_groups)."
        },
        "groups": {
          "type": ["array", "null"],
          "items": { "type": "string" },
          "default": null,
          "description": "Group levels to show as columns (AE-CFG-005). Null derives every level found in group_col, sorted; configured levels missing from the data are dropped with a console warning. A single group hides the Total and Difference columns (AE-USER-019)."
        },
        "colors": {
          "type": "array",
          "items": { "type": "string" },
          "description": "Group colors assigned by column order (AE-CFG-006); the Total column always renders gray. The default palette carries no yellow."
        },
        "filters": {
          "$ref": "#/$defs/filterList",
          "description": "Filter controls (AE-USER-018): column names or { value_col, label, type, start, all, multiple } specs: `start` is the value the filter opens on, `all` (default true) is whether an \"All\" option is offered, and `multiple` (default false) makes it a checkbox multiselect. Type 'event' narrows the events counted; type 'participant' narrows the analysis population and its denominators. Defaults to the four ADAE event filters — seriousness, severity, relationship, outcome; filters whose column is absent or single-valued are dropped with a console warning."
        },
        "details": {
          "$ref": "#/$defs/fieldList",
          "description": "Columns for the details drill-down listing (AE-REG-024): null shows every input column."
        },
        "variable_options": {
          "type": ["object", "null"],
          "default": null,
          "description": "Valid alternative columns for the primary mappings (AE-CFG-004): keys id, major, minor, group, each an array of column names. Two or more options for a mapping draw a re-mapping control; the current mapping is always offered even when not listed (AE-REG-044).",
          "properties": {
            "id": { "type": "array", "items": { "type": "string" } },
            "major": { "type": "array", "items": { "type": "string" } },
            "minor": { "type": "array", "items": { "type": "string" } },
            "group": { "type": "array", "items": { "type": "string" } }
          }
        },
        "placeholder_flag": {
          "type": "object",
          "description": "How placeholder rows for AE-free participants are identified (AE-DATA-001): value_col (null defaults to major_col) and the values that mark a placeholder.",
          "properties": {
            "value_col": { "type": ["string", "null"], "default": null },
            "values": {
              "type": "array",
              "items": { "type": "string" },
              "default": ["", "NA"]
            }
          }
        },
        "max_prevalence": {
          "type": "number",
          "default": 0,
          "description": "Initial minimum-prevalence filter value in percent (AE-USER-001): rows where every group rate is below the threshold are hidden."
        },
        "max_groups": {
          "type": "number",
          "default": 6,
          "description": "Most group columns the table will draw; more levels than this throws."
        },
        "total_col": {
          "type": "boolean",
          "default": true,
          "description": "Draw the all-groups Total column (AE-REG-037). Suppressed automatically when only one group shows."
        },
        "group_cols": {
          "type": "boolean",
          "default": true,
          "description": "Draw the per-group rate columns. Disabling leaves a Total-only table and suppresses the Difference column (AE-REG-037)."
        },
        "diff_col": {
          "type": "boolean",
          "default": true,
          "description": "Draw the Difference Between Groups column (AE-USER-013). Needs two or more shown groups."
        },
        "pref_terms": {
          "type": "boolean",
          "default": false,
          "description": "Start with every Preferred Term row expanded instead of collapsed."
        },
        "summarize_by": {
          "type": "string",
          "enum": ["participant", "event"],
          "default": "participant",
          "description": "Summary basis (AE-USER-006): participants with at least one event over the group's distinct participants, or event counts over the group's event total (AE-REG-033, AE-REG-035)."
        },
        "validation": {
          "type": "boolean",
          "default": false,
          "description": "Adds a summarized-data CSV download named major-minor-summarize_by (AE-CFG-009, AE-USER-020)."
        },
        "plot_settings": {
          "type": "object",
          "description": "Inline row-plot geometry (AE-CFG-008): height, width, and point radius in pixels, with margin / diff_margin {left, right} insets for the rate and difference plots (AE-REG-046).",
          "properties": {
            "height": { "type": "number", "default": 15 },
            "width": { "type": "number", "default": 200 },
            "radius": { "type": "number", "default": 7 },
            "margin": { "type": "object", "default": { "left": 40, "right": 40 } },
            "diff_margin": { "type": "object", "default": { "left": 5, "right": 5 } }
          }
        },
        "page_size": {
          "type": "number",
          "default": 10,
          "description": "Rows per page in the details drill-down listing."
        }
      }
    }
  },
  "$defs": {
    "fieldList": {
      "type": ["array", "null"],
      "items": {
        "anyOf": [
          { "type": "string" },
          {
            "type": "object",
            "required": ["value_col"],
            "properties": {
              "value_col": { "type": "string" },
              "label": { "type": "string" }
            }
          }
        ]
      }
    },
    "filterList": {
      "type": ["array", "null"],
      "items": {
        "anyOf": [
          { "type": "string" },
          {
            "type": "object",
            "required": ["value_col"],
            "properties": {
              "value_col": { "type": "string" },
              "label": { "type": "string" },
              "type": { "type": "string", "enum": ["event", "participant"], "default": "event" },
              "start": {
                "type": ["string", "number", "boolean", "array", "null"],
                "default": null,
                "description": "The value the filter opens on; an array of values for a `multiple` filter. 0 and false are real values, not an absent start. A value the data lacks is dropped with a console warning."
              },
              "all": {
                "type": "boolean",
                "default": true,
                "description": "Whether the filter offers an \"All\" option. false removes it, and the filter then always holds one value: the first, unless `start` names another."
              },
              "multiple": {
                "type": "boolean",
                "default": false,
                "description": "Render a checkbox multiselect instead of a dropdown; the rows shown are those matching any selected value."
              }
            }
          }
        ]
      }
    }
  }
}
